import type { CSSProperties } from 'react';
import General from './screens/General';
import Users from './screens/Users';
import Admissions from './screens/Admissions';
import FinancialAid from './screens/FinancialAid';
import { ModuleSectionNav, type ModuleSectionNavItem } from '../../components/navigation/ModuleSectionNav';
import { YstRouteTransition } from '../../components/motion/YstRouteTransition';
import { useShellProfile } from '../../state/useShellProfile';
import {
  STAFF_SETTINGS_SECTION_ORDER,
  getStaffSettingsSectionRoute,
  type StaffSettingsSection,
} from '../../settings/settingsEntrySystem';
import './StaffSettingsSemanticPage.css';

type Props = { section: StaffSettingsSection };

const SETTINGS_PARITY_THEME = {
  '--spacing': '0.2857142857rem',
  '--color-primary': '#2697a2',
} as const;

const TAB_LABELS: Record<Exclude<StaffSettingsSection, 'integrations'>, string> = {
  general: 'General',
  users: 'Users',
  admissions: 'Admissions',
  'financial-aid': 'Financial Aid',
};

const TABS: ReadonlyArray<ModuleSectionNavItem> = STAFF_SETTINGS_SECTION_ORDER.map((section, index) => ({
  id: section,
  label: TAB_LABELS[section],
  to: getStaffSettingsSectionRoute(section),
  ...(index === 0 ? { end: true } : {}),
}));

function TabNavigation({ active }: { active: StaffSettingsSection }) {
  return (
    <div className="border-gray-200">
      <ModuleSectionNav
        items={[...TABS]}
        activeId={active}
        ariaLabel="Settings sections"
        testIds={{ itemPrefix: 'settings-tab' }}
      />
    </div>
  );
}

function SectionSurface({ section }: { section: StaffSettingsSection }) {
  if (section === 'users') {
    return (
      <section data-testid="settings-users-root">
        <div data-testid="settings-users-role-governance-visual-surface">
          <Users />
        </div>
      </section>
    );
  }

  if (section === 'admissions') {
    return (
      <section data-testid="settings-admissions-root">
        <Admissions />
      </section>
    );
  }

  if (section === 'financial-aid') {
    return (
      <section data-testid="settings-financial-aid-root">
        <FinancialAid />
      </section>
    );
  }

  return (
    <section data-testid="settings-general-root">
      <div data-testid="settings-general-governance-visual-surface">
        <General />
      </div>
    </section>
  );
}

export function StaffSettingsSemanticPage({ section }: Props) {
  const { effectivePersona } = useShellProfile();
  const sectionRoute = getStaffSettingsSectionRoute(section);
  return (
    <section
      data-testid="settings-semantic-surface-root"
      data-extracted-from="owned:ystnj/settings/semantic-v2"
      data-effective-persona={effectivePersona}
      data-settings-entry-route={sectionRoute}
      className="settings-semantic-root min-h-screen antialiased"
      style={SETTINGS_PARITY_THEME as CSSProperties}
    >
      <main className="mx-auto w-full max-w-[1440px] space-y-5">
        <TabNavigation active={section} />
        <YstRouteTransition className="mb-20">
          <SectionSurface section={section} />
        </YstRouteTransition>
      </main>
    </section>
  );
}
